import { createWork4MeAgent } from '@/api/agents-api';
import { listProcessSkillOptions } from '@/api/processes-api';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Work4MeAgentForm } from './Work4MeAgentForm';

interface Work4MeAgentCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (agentId: number) => void;
}

export function Work4MeAgentCreate({ open, onOpenChange, onCreated }: Work4MeAgentCreateProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<number>>(new Set());

  const skillOptionsQuery = useQuery({
    queryKey: ['process-skill-options'],
    queryFn: listProcessSkillOptions,
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: () => createWork4MeAgent({
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      instructions: instructions.trim(),
      skillIds: [...selectedSkillIds].sort((a, b) => a - b),
      isEnabled,
    }),
    onSuccess: async (created) => {
      toast.success(`Created "${created.name}".`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['process-agent-options'] }),
        queryClient.invalidateQueries({ queryKey: ['work4me-agent', created.id] }),
      ]);
      onCreated?.(created.id);
      onOpenChange(false);
      setName('');
      setDescription('');
      setInstructions('');
      setIsEnabled(true);
      setSelectedSkillIds(new Set());
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create agent.';
      toast.error(message);
    },
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          setName('');
          setDescription('');
          setInstructions('');
          setIsEnabled(true);
          setSelectedSkillIds(new Set());
        }
      }}
    >
      <SheetContent className="w-screen sm:w-[640px] flex flex-col px-0">
        <SheetHeader className="px-4">
          <SheetTitle>Create Agent</SheetTitle>
          <SheetDescription>Create a member-scoped Work4Me agent for this workspace.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <Work4MeAgentForm
            name={name}
            description={description}
            instructions={instructions}
            isEnabled={isEnabled}
            selectedSkillIds={selectedSkillIds}
            skillOptions={skillOptionsQuery.data?.items ?? []}
            isDirty={true}
            isSaving={createMutation.isPending}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onInstructionsChange={setInstructions}
            onIsEnabledChange={setIsEnabled}
            onSkillToggle={(skillId, checked) => {
              setSelectedSkillIds((prev) => {
                const next = new Set(prev);
                if (checked) {
                  next.add(skillId);
                } else {
                  next.delete(skillId);
                }
                return next;
              });
            }}
            onSave={() => createMutation.mutate()}
            submitLabel="Create Agent"
            submittingLabel="Creating..."
            editableHint="New agent configuration is saved when created."
          />
        </div>

        <SheetFooter className="px-4">
          <div className="w-full">
            <Separator className="mb-4" />
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

